import { useCallback, useMemo } from 'react';
import type { EventPageVisibility } from '@graphql/generated-base';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useIsNewStackEvent } from '@shared/utils/eventInfo/useIsNewStackEvent';
import { useWebsiteVersion } from '@shared/hooks/websiteVersion';
import type { UseWebsiteVersionOptions } from '@shared/hooks/websiteVersion';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { filterMigratedLegacyPages } from '@shared/utils/migratedCustomPages';
import { useWebsitePagesByRoleQuery, useWebsitePagesByRoleSectionsQuery } from './useWebsitePagesByRole.graphql.generated';
import type { WebsitePagesByRoleQuery } from './useWebsitePagesByRole.graphql.generated';
import { fromV1Pages, fromV2Sections, withV1Fallbacks } from './useWebsitePagesByRole.utils';
import type { WebsitePage, WebsitePageRole } from './useWebsitePagesByRole.types';

export type UseWebsitePagesByRoleOptions = Readonly<{
  /** The event; defaults to the one `useEventInfo` resolves (the route's event). */
  eventHandle?: string;
  /**
   * Read as the event admin: includes the pages guests can't see. Guests (false) get only what
   * they may see, and nothing for a V2 site that isn't published.
   */
  admin: boolean;
  /**
   * V1: every page, the hidden ones too, as the admin's page list shows them. Otherwise the pages
   * the guest site renders. V2 sections are always all of them.
   */
  includeHidden?: boolean;
  /** V2: these roles' V1 pages, where the site has no section for them. */
  v1FallbackRoles?: ReadonlyArray<WebsitePageRole>;
  /**
   * V2: the site the host picked, or the published one, for what guests see or are sent. Defaults to the
   * published site for a guest's read (`admin: false`) and the picked one for an admin's. See `useWebsiteVersion`.
   */
  site?: UseWebsiteVersionOptions['site'];
  skip?: boolean;
}>;

export type WebsitePagesByRole = Readonly<{
  /** The event is on Website V2, and `pages` are its site's sections (none until it has a site). */
  isWebsiteV2: boolean;
  /** The V2 site is published. Null for a V1 event. */
  isWebsiteV2Published: boolean | null;
  /** The V2 site the pages are read from (`useWebsiteVersion`'s); null for V1, or a V2 event with no site. */
  websiteV2SiteId: string | null;
  /**
   * In site order. V1: the guest site's enabled pages (every page with `includeHidden`). V2: the
   * site's pages, the sections other than the header.
   */
  pages: ReadonlyArray<WebsitePage>;
  /** The page with this role (the first, for `custom`). */
  getPage: (role: WebsitePageRole) => WebsitePage | undefined;
  isLoading: boolean;
  /** For `useWebsitePrivacy`, which reads the same request. */
  event: WebsitePagesByRoleQuery['eventByName'] | undefined;
}>;

/**
 * The event's website pages by role, in the shape callers already use, whichever website version
 * the event is on: a V1 event's pages, or its active V2 site's sections, which win. Callers never
 * deal with website versions. See doc/website-v2/legacy-design-compat.md, and useEventWebsiteDesign
 * for the same pattern on the design.
 */
export const useWebsitePagesByRole = ({
  eventHandle: eventHandleOverride,
  admin,
  includeHidden = false,
  v1FallbackRoles,
  site: siteChoice,
  skip = false,
}: UseWebsitePagesByRoleOptions): WebsitePagesByRole => {
  const eventInfo = useEventInfo();
  const eventHandle = eventHandleOverride ?? eventInfo.eventHandle ?? '';
  const isNewStackEvent = useIsNewStackEvent();

  // A guest's read is filtered by their session (specific-guests pages, published sites), so it is
  // client-only, as the V1 post-RSVP query is: a server render would cache the anonymous answer, and
  // nothing refetches it once the guest is identified.
  const ssr = admin;
  const version = useWebsiteVersion({ eventHandle: eventHandleOverride, ssr, site: siteChoice ?? (admin ? 'selected' : 'published'), skip: skip || !eventHandle });
  const { isWebsiteV2 } = version;
  const siteId = version.site?.id ?? null;
  // A V2 event reads its V1 pages only to fall back on.
  // Keyed by value: callers pass the roles inline.
  const v1FallbackRolesKey = v1FallbackRoles?.join(',') ?? '';
  const fallbackRoles = useMemo(() => (v1FallbackRolesKey ? (v1FallbackRolesKey.split(',') as WebsitePageRole[]) : []), [v1FallbackRolesKey]);
  const needsV1Pages = !isWebsiteV2 || fallbackRoles.length > 0;

  // Not before the version is known, so a V2 event that needs none doesn't start it.
  const { data, loading } = useWebsitePagesByRoleQuery({
    variables: { eventHandle, admin },
    batchMode: 'fast',
    skip: skip || !eventHandle || version.isLoading || !needsV1Pages,
    ssr,
  });
  const event = data?.eventByName;

  const { data: sectionsData, loading: sectionsLoading } = useWebsitePagesByRoleSectionsQuery({
    variables: { siteId: siteId ?? '' },
    batchMode: 'fast',
    skip: skip || !siteId,
    ssr,
  });

  // V1: the pages the guest site renders, as the link pickers have always listed them.
  const dedupedPages = useMemo(() => filterMigratedLegacyPages(event?.pages ?? []), [event?.pages]);
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: dedupedPages, skip: skip || !needsV1Pages });
  const enabledPages = useMemo(() => guestSitePages.filter(page => !page.disabled), [guestSitePages]);
  const { isDefaultPage } = useDefaultPage(enabledPages);
  const containerVisibilityById = useMemo(
    () => new Map<string, EventPageVisibility>((event?.pageContainers ?? []).map(container => [container.id, container.visibility])),
    [event?.pageContainers],
  );
  const v1Pages = useMemo(
    () =>
      needsV1Pages ? fromV1Pages({ eventHandle, pages: includeHidden || isWebsiteV2 ? dedupedPages : enabledPages, containerVisibilityById, isDefaultPage, isNewStackEvent }) : [],
    [needsV1Pages, eventHandle, includeHidden, isWebsiteV2, dedupedPages, enabledPages, containerVisibilityById, isDefaultPage, isNewStackEvent],
  );

  const sections = sectionsData?.carbonWebsiteSiteSections;
  // A V2 event without a site the viewer may read (none generated yet, or unpublished for a guest)
  // has no pages: its V1 pages are not on any site guests can open.
  const pages = useMemo(() => {
    if (!isWebsiteV2) return v1Pages;
    if (!sections || !siteId) return [];
    const sitePages = fromV2Sections(eventHandle, sections);
    return fallbackRoles.length ? withV1Fallbacks(sitePages, v1Pages, fallbackRoles) : sitePages;
  }, [isWebsiteV2, v1Pages, sections, siteId, eventHandle, fallbackRoles]);

  const getPage = useCallback((role: WebsitePageRole) => pages.find(page => page.role === role), [pages]);

  const isLoading =
    !skip &&
    (version.isLoading ||
      (needsV1Pages && loading && !data) ||
      (!!siteId && sectionsLoading && !sectionsData) ||
      // The guest site's pages are filtered against the event's info: until it arrives there are none.
      (needsV1Pages && !eventInfo.eventInfo && eventInfo.loading));

  return useMemo(
    () => ({
      isWebsiteV2,
      isWebsiteV2Published: isWebsiteV2 ? (version.site?.isPublished ?? false) : null,
      websiteV2SiteId: siteId,
      pages,
      getPage,
      isLoading,
      event,
    }),
    [isWebsiteV2, version.site, siteId, event, pages, getPage, isLoading],
  );
};
